import { MANIFEST_TO_PERMISSION_MODULE } from "@/features/manifest/model/permissionModuleMap";
import type { ModulesEnabled } from "@/features/manifest/model/types";
import { computeVisibleSettingsSections } from "@/features/settings/model/useVisibleSettingsSections";

import { NAV_GROUPS, UNGROUPED_ITEMS, type NavGroupId, type NavItem } from "./sidebarNav";

export type NavVisibilityCtx = {
  isControl: boolean;
  fullAccess: boolean;
  canView: (module: string) => boolean;
  hasCapability: (module: string, capability: "view" | "create" | "edit" | "delete") => boolean;
  userRole: string | undefined;
  schedulingEnabled: boolean;
  modulesEnabled: ModulesEnabled | undefined;
  grantManagementEnabled?: boolean;
  inventoryEnabled?: boolean;
};

export function isNavItemVisible(item: NavItem, ctx: NavVisibilityCtx): boolean {
  if (item.requiresControl && !ctx.isControl) return false;
  if (item.settingsHub) {
    return (
      computeVisibleSettingsSections({
        fullAccess: ctx.fullAccess,
        canView: ctx.canView,
        hasCapability: ctx.hasCapability,
        userRole: ctx.userRole,
      }).length > 0
    );
  }
  const matrixModule =
    item.permissionMatrixModule ??
    (item.requiresModule ? MANIFEST_TO_PERMISSION_MODULE[item.requiresModule] : undefined);
  if (matrixModule && !ctx.fullAccess) {
    if (item.visibleWithAnyCrud) {
      const crud = ["view", "create", "edit", "delete"] as const;
      if (!crud.some((cap) => ctx.hasCapability(matrixModule, cap))) return false;
    } else if (!ctx.canView(matrixModule)) {
      return false;
    }
  }
  if (item.requiresRole && !matrixModule && !(ctx.userRole && item.requiresRole.includes(ctx.userRole))) {
    return false;
  }
  if (item.requiresModule && ctx.modulesEnabled) {
    const moduleOn =
      item.requiresModule === "scheduling"
        ? ctx.schedulingEnabled
        : ctx.modulesEnabled[item.requiresModule as keyof ModulesEnabled];
    if (!moduleOn) return false;
  }
  if (item.nestedGrants) {
    const pipelineOn =
      Boolean(ctx.modulesEnabled?.grant_management) && ctx.grantManagementEnabled !== false;
    const fundGrantOn = Boolean(ctx.modulesEnabled?.accounting);
    if (!pipelineOn && !fundGrantOn) return false;
    return true;
  }
  if (item.to === "/grants" && ctx.grantManagementEnabled === false) return false;
  if (item.to === "/inventory" && ctx.inventoryEnabled === false) return false;
  return true;
}

export function visibleUngroupedItems(ctx: NavVisibilityCtx): NavItem[] {
  return UNGROUPED_ITEMS.filter((item) => isNavItemVisible(item, ctx));
}

export function visibleItemsInGroup(groupId: NavGroupId, ctx: NavVisibilityCtx): NavItem[] {
  const group = NAV_GROUPS.find((g) => g.id === groupId);
  if (!group) return [];
  return group.items.filter((item) => isNavItemVisible(item, ctx));
}
